import React, { useState } from 'react'
import { View, TouchableOpacity, Image, StyleSheet } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Text, EventParticipants, Icon, PickerModal } from '@components'
import { Theme } from '@theme'
import dayjs from 'dayjs'
import { formatDuration, Scope, showApiErrorToast, showSuccessToast } from '@utils'
import {
  CalendarEventUpcomingNotificationData,
  ContactRequestAnswer,
  ContactRequestAnsweredNotificationData,
  EventOrganizerMessageNotificationData,
  EventReminderNotificationData,
  KeepContactReminderNotificationData,
  NotificationType,
  ScheduledMessageSentNotificationData,
  ScheduledMessageUpcomingNotificationData,
  type Notification,
} from '../../../hooks/api/queries'
import { useGetProfileById } from '../../../hooks/api/queries/useGetProfileById'
import { useGetKeepContactById } from '../../../hooks/api/queries/useGetKeepContactById'
import { useGetCalendarEventById } from '../../../hooks/api/queries/useGetCalendarEventById'
import { useGetScheduledMessageById } from '../../../hooks/api/queries/useGetScheduledMessageById'
import { usePaginateScheduledMessagesByRecipientId } from '../../../hooks/api/queries/usePaginateScheduledMessagesByRecipientId'
import { defaultImage } from '@assets'
import { useCreationStackIntegration } from '@hooks'
import { useLanguage } from '@context'
import { getNotificationVisual } from './notificationVisuals'
import { NotificationActions, type NotificationAction } from './NotificationActions'
import { MutualContactsLine } from './MutualContactsLine'
import { richParts, RichSentence } from './richText'
import { dialable, useReachOut } from '../useReachOut'
import {
  useScheduleBirthdayWish,
  useSendBirthdayWish,
} from '../../menu/birthdays/useSendBirthdayWish'
import {
  ageThisYear,
  daysLate,
  dueLabel,
  postponedDate,
  type PostponeChoice,
} from '../../../utils/notificationDue'

type FormattedNotification = Notification & {
  message: string
  contactId?: string
  contactIds?: string[]
  contactRequestId?: string
  accepted?: boolean
  isRead: boolean
  description?: string
}

interface NotificationItemProps {
  notification: FormattedNotification
  /** Marks it read. */
  onPress: () => void
  onAccept?: (scope: Scope) => void
  onReject?: () => void
  /** « C'est fait » on a follow-up. */
  onDone?: () => void
  /** « Reporter » a follow-up to this date. */
  onPostpone?: (date: string) => void
}

/** Scheduled birthday wishes leave at this time, the day of the birthday. */
const WISH_HOUR = 9

/** « Prévenir d'un retard » is offered from this long before a rendez-vous… */
const WARN_LATE_BEFORE_MIN = 3 * 60
/** …until this long after it started. */
const WARN_LATE_AFTER_MIN = 60

const capitalize = (text: string) => (text ? text.charAt(0).toUpperCase() + text.slice(1) : text)

type Person = { id: string; fullName: string }

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onPress,
  onAccept,
  onReject,
  onDone,
  onPostpone,
}) => {
  const navigation = useNavigation()
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)
  const [isPostponeOpen, setIsPostponeOpen] = useState(false)
  const [isWarnPickerOpen, setIsWarnPickerOpen] = useState(false)
  const [isSchedulingWish, setIsSchedulingWish] = useState(false)

  const {
    showScheduledMessageCreation,
    showReminderCreation,
    showKeepContactCreation,
    isCreationStackEnabled,
  } = useCreationStackIntegration()

  const { type } = notification
  const isKeep = type === NotificationType.KEEP_CONTACT_REMINDER
  const isEvent = type === NotificationType.CALENDAR_EVENT_UPCOMING
  const isScheduled = type === NotificationType.SCHEDULED_MESSAGE_UPCOMING
  const isSent = type === NotificationType.SCHEDULED_MESSAGE_SENT
  const isBirthday = type === NotificationType.BIRTHDAY_UPCOMING
  const isPending = type === NotificationType.CONTACT_REQUEST_CREATED
  const isAnswered = type === NotificationType.CONTACT_REQUEST_ANSWERED

  // A public-event notification (reminder / organizer message) opens the
  // PublicEvent screen rather than the agenda; keep it alongside the
  // calendar-event (CALENDAR_EVENT_UPCOMING) handling above.
  const isPublicEvent =
    type === NotificationType.EVENT_REMINDER ||
    type === NotificationType.EVENT_ORGANIZER_MESSAGE

  const keepData = notification.data as KeepContactReminderNotificationData
  // The automatic follow-up after an event comes with its own text
  // (« Relancer Julien ? » / « Vous vous êtes rencontrés à … »).
  const isEventFollowUp = isKeep && Boolean(keepData.pushCopy)
  const eventData = notification.data as CalendarEventUpcomingNotificationData
  const scheduledData = notification.data as ScheduledMessageUpcomingNotificationData
  const sentData = notification.data as ScheduledMessageSentNotificationData
  const answeredData = notification.data as ContactRequestAnsweredNotificationData
  const birthDate = (notification.data as { birthDate?: string }).birthDate
  const isEve = isBirthday && Boolean((notification.data as { eve?: boolean }).eve)

  // Who or what the notification is about.
  const personId = (isSent ? sentData.contactId : notification.contactId) || ''
  const { data: person } = useGetProfileById(personId)
  // Its name is only shown on an ordinary follow-up.
  const { data: keepContact } = useGetKeepContactById(
    isKeep && !isEventFollowUp ? keepData.keepContactId : '',
  )
  const { data: event } = useGetCalendarEventById(isEvent ? eventData.calendarEventId : '')
  const { data: scheduledMessage } = useGetScheduledMessageById(
    isScheduled ? scheduledData.scheduledMessageId : '',
  )

  const { openConversation, call, sms, email, route } = useReachOut()
  const sendBirthdayWish = useSendBirthdayWish()
  const scheduleBirthdayWish = useScheduleBirthdayWish()

  const name = person?.fullName || person?.firstName || ''
  const isGhost = Boolean(person?.isGhost)
  const phone = isGhost ? dialable(person?.contacts?.phone) : null
  const emailAddress = isGhost ? person?.contacts?.email?.trim() || null : null
  const visual = getNotificationVisual(type, theme, notification.accepted)
  const nav = navigation as any

  // Wishes already scheduled for tomorrow's birthday (the day before only).
  const wishSendAt = dayjs().add(1, 'day').hour(WISH_HOUR).minute(0).second(0).millisecond(0)
  const { flatData: personScheduled = [] } = usePaginateScheduledMessagesByRecipientId(
    isEve && personId && person && !isGhost ? personId : '',
  )
  const scheduledWish = isEve
    ? personScheduled.find((message) => dayjs(message.scheduledDate).isSame(wishSendAt, 'day'))
    : undefined

  const openProfile = () => {
    if (!personId) return
    nav.navigate('Profile', { screen: 'DetailsGhostProfile', params: { profileId: personId } })
  }

  const openEvent = () => {
    if (isCreationStackEnabled) {
      showReminderCreation({ isCreationStack: true, eventId: eventData.calendarEventId })
    } else {
      nav.navigate('Menu', {
        screen: 'Calendar',
        params: { screen: 'AddEvent', params: { eventId: eventData.calendarEventId } },
      })
    }
  }

  // Event reminders and organizer messages open the public event page.
  const openPublicEvent = () => {
    const data = notification.data as
      | EventReminderNotificationData
      | EventOrganizerMessageNotificationData
    nav.navigate('PublicEvent', { code: data.eventCode })
  }

  const openScheduledMessage = (scheduledMessageId: string) => {
    if (isCreationStackEnabled) {
      showScheduledMessageCreation({ scheduledMessageId, isEdit: true }, 1)
    } else {
      nav.navigate('Menu', {
        screen: 'ScheduledMessages',
        params: {
          screen: 'ComposeMessage',
          params: { scheduledMessageId, isEdit: true },
        },
      })
    }
  }

  const openKeepContact = () => {
    if (isCreationStackEnabled) {
      showKeepContactCreation({ isCreationStack: true, keepContactId: keepData.keepContactId })
    } else {
      nav.navigate('Menu', {
        screen: 'KeepContact',
        params: { screen: 'AddKeep', params: { keepContactId: keepData.keepContactId } },
      })
    }
  }

  const openSentConversation = () => {
    nav.navigate('Messages', {
      screen: sentData.groupId ? 'GroupConversation' : 'Conversation',
      params: { conversationId: sentData.conversationId },
    })
  }

  // Tapping the card marks it read and opens what it is about.
  const handlePress = () => {
    onPress()
    if (isBirthday || isPending || isAnswered) openProfile()
    else if (isEvent) openEvent()
    else if (isScheduled) openScheduledMessage(scheduledData.scheduledMessageId)
    else if (isSent) openSentConversation()
    // The person's card is where the meeting place and the reminder are.
    else if (isEventFollowUp) openProfile()
    else if (isKeep) openKeepContact()
    else if (isPublicEvent) openPublicEvent()
  }

  const withRead = (action: () => void) => () => {
    onPress()
    action()
  }

  // « Programmer mes vœux »: written now, sent tomorrow at 9:00, then shown to be read or changed.
  const scheduleWish = async () => {
    if (isSchedulingWish) return
    onPress()
    setIsSchedulingWish(true)
    try {
      const scheduledMessageId = await scheduleBirthdayWish(
        { id: personId, fullName: name, birthDate: birthDate ?? '', scope: person?.scope },
        wishSendAt,
      )
      showSuccessToast(
        t('toast.success'),
        t('notifications.toasts.wishScheduled', { time: wishSendAt.format('HH:mm') }),
      )
      openScheduledMessage(scheduledMessageId)
    } catch (error) {
      showApiErrorToast(t, error)
    } finally {
      setIsSchedulingWish(false)
    }
  }

  // The people a late arrival is told to: the organizer and the other participants.
  const eventPeople: Person[] = []
  if (isEvent && event) {
    const candidates = [
      ...(event.organizer ? [event.organizer] : []),
      ...(event.participants ?? []).filter((participant) => !participant.isGhost),
    ]
    for (const candidate of candidates) {
      if (!candidate?.id || candidate.id === notification.recipientId) continue
      if (eventPeople.some((known) => known.id === candidate.id)) continue
      eventPeople.push({ id: candidate.id, fullName: candidate.fullName?.trim() || '' })
    }
  }
  const warnLate = (personToWarn: string) => {
    onPress()
    void openConversation(personToWarn, t('notifications.details.lateMessage'))
  }

  // Title
  const bold = (key: string, params: Record<string, string | number> = {}) => (
    <RichSentence
      parts={richParts(t, key, { ...params, name }, ['name'])}
      color={theme.colors.black}
      style={styles.flexText}
    />
  )
  const plainTitle = (text: string) => (
    <Text type="bodyBold" color={theme.colors.black} style={styles.flexText}>
      {text}
    </Text>
  )
  const age = isBirthday
    ? ageThisYear(birthDate, isEve ? dayjs().add(1, 'day') : dayjs())
    : null
  let title: React.ReactNode
  if (isEventFollowUp) title = plainTitle(notification.message)
  else if (isKeep && name) title = bold('notifications.titles.keepContact')
  else if (isScheduled && name) title = bold('notifications.titles.scheduledMessage')
  else if (isBirthday && name) {
    title = isEve
      ? bold(
          age ? 'notifications.titles.birthdayTomorrowAge' : 'notifications.titles.birthdayTomorrow',
          age ? { age } : {},
        )
      : bold(age ? 'notifications.titles.birthdayAge' : 'notifications.titles.birthday', age ? { age } : {})
  } else if (isPending && name) title = bold('notifications.titles.request')
  else if (isAnswered && name) title = bold('notifications.titles.accepted')
  else if (isSent && sentData.groupId) {
    title = (
      <RichSentence
        parts={richParts(
          t,
          'notifications.titles.sentGroup',
          { name: sentData.groupTitle?.trim() || '' },
          ['name'],
        )}
        color={theme.colors.black}
        style={styles.flexText}
      />
    )
  } else if (isSent && name) title = bold('notifications.titles.sent')
  else if (isEvent) title = plainTitle(event?.title?.trim() || t('notifications.titles.event'))
  else title = plainTitle(notification.message)

  // When it is due, in plain words (in red when a follow-up is late)
  let when: string | null = null
  const late = isKeep ? daysLate(keepData.keepDate) : 0
  if (isKeep) {
    when = late > 0 ? t('notifications.when.late', { count: late }) : dueLabel(keepData.keepDate, t, { withTime: false })
  } else if (isEvent) when = dueLabel(eventData.startDateTime, t, { withTime: !event?.allDay })
  else if (isScheduled) when = dueLabel(scheduledData.scheduledAt, t, { withTime: true })

  // News show how long ago they happened.
  const showAgo = isPending || isAnswered || isSent
  const agoOf = isPending ? notification.createdAt : notification.eventAt || notification.createdAt

  // One line of context
  const detailText = (text: string, extra?: object) => (
    <Text type="bodySmall" color={theme.colors.greyBlue} style={extra}>
      {text}
    </Text>
  )
  let detail: React.ReactNode = null
  // The text of an event's follow-up, reminder or organizer message: for a
  // message sent as notification only, the card is the one place to read it.
  if ((isEventFollowUp || isPublicEvent) && notification.description?.trim()) {
    detail = detailText(notification.description.trim())
  } else if (isKeep) {
    const lastMessage = person?.lastInteractionAt
      ? t('notifications.lastMessage', { ago: dayjs(person.lastInteractionAt).fromNow() })
      : null
    const text = [keepContact?.name?.trim(), lastMessage].filter(Boolean).join(' · ')
    if (text) detail = detailText(capitalize(text))
  } else if (isEvent) {
    const place = [event?.location?.place, event?.location?.city]
      .map((part) => part?.trim())
      .filter((part, index, all): part is string => Boolean(part) && all.indexOf(part) === index)
      .join(', ')
    if (place) {
      detail = (
        <View style={styles.detailRow}>
          <Icon name="mapPin" size={theme.iconSize.extraSmall} color={theme.colors.greyBlue} />
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {place}
          </Text>
        </View>
      )
    }
  } else if (isScheduled && scheduledMessage?.message?.trim()) {
    detail = detailText(`« ${scheduledMessage.message.trim()} »`, styles.quote)
  } else if (isSent && sentData.preview?.trim()) {
    detail = detailText(`« ${sentData.preview.trim()} »`, styles.quote)
  } else if (isBirthday && isGhost) {
    detail = detailText(
      isEve
        ? t('notifications.details.eveGhost')
        : phone
          ? t('notifications.details.ghostPhone')
          : t('notifications.details.ghostNoPhone'),
    )
  } else if (isEve && person) {
    detail = detailText(
      scheduledWish
        ? t('notifications.details.eveScheduled', { time: dayjs(scheduledWish.scheduledDate).format('HH:mm') })
        : t('notifications.details.eve', { time: wishSendAt.format('HH:mm') }),
    )
  } else if (isAnswered) {
    detail = detailText(
      answeredData.scope
        ? t('notifications.details.acceptedScope', {
            scope:
              answeredData.scope === Scope.PROFESSIONAL
                ? t('notifications.scope.PROFESSIONAL')
                : t('notifications.scope.PRIVATE'),
          })
        : t('notifications.details.acceptedNoScope'),
    )
  } else if (isPending && notification.mutualContacts?.count) {
    detail = <MutualContactsLine mutual={notification.mutualContacts} />
  } else if (isPending && personId) {
    detail = (
      <TouchableOpacity onPress={openProfile} style={styles.detailRow} accessibilityRole="link">
        <Icon name="user" size={theme.iconSize.extraSmall} color={theme.colors.primary} />
        <Text type="bodySmallBold" color={theme.colors.primary}>
          {t('notifications.actions.seeProfile')}
        </Text>
      </TouchableOpacity>
    )
  }

  // Someone without a KeepTouch account: a call, a text or an e-mail.
  const reachGhost: NotificationAction[] = phone
    ? [
        { key: 'call', label: t('notifications.actions.call'), icon: 'call', onPress: withRead(() => call(phone)) },
        { key: 'sms', label: t('notifications.actions.sms'), icon: 'message', onPress: withRead(() => sms(phone)) },
      ]
    : emailAddress
      ? [
          {
            key: 'email',
            label: t('notifications.actions.email'),
            icon: 'message',
            onPress: withRead(() => email(emailAddress)),
          },
        ]
      : [{ key: 'see-card', label: t('notifications.actions.seeCard'), icon: 'user', onPress: withRead(openProfile) }]

  // What can be done right away (style B: one outlined button, the rest in text)
  let actions: NotificationAction[] = []
  if (isKeep && personId) {
    const reach: NotificationAction[] = isGhost
      ? reachGhost
      : [
          {
            key: 'write',
            label: t('notifications.actions.write'),
            icon: 'messageCircle',
            onPress: withRead(() => void openConversation(personId)),
          },
        ]
    actions = [
      ...reach,
      { key: 'done', label: t('notifications.actions.done'), icon: 'check', onPress: () => onDone?.() },
      {
        key: 'postpone',
        label: t('notifications.actions.postpone'),
        icon: 'clock',
        onPress: () => setIsPostponeOpen(true),
      },
    ]
  } else if (isEvent) {
    const place = [event?.location?.place, event?.location?.city]
      .map((part) => part?.trim())
      .filter((part, index, all): part is string => Boolean(part) && all.indexOf(part) === index)
      .join(', ')
    const minutesToStart = dayjs(eventData.startDateTime).diff(dayjs(), 'minute')
    const canWarnLate =
      !event?.allDay &&
      eventPeople.length > 0 &&
      minutesToStart <= WARN_LATE_BEFORE_MIN &&
      minutesToStart >= -WARN_LATE_AFTER_MIN
    actions = [
      ...(place
        ? [{ key: 'route', label: t('notifications.actions.route'), icon: 'locate' as const, onPress: withRead(() => route(place)) }]
        : []),
      ...(canWarnLate
        ? [
            {
              key: 'warn-late',
              label: t('notifications.actions.warnLate'),
              icon: 'messageCircle' as const,
              onPress: () =>
                eventPeople.length === 1 ? warnLate(eventPeople[0].id) : setIsWarnPickerOpen(true),
            },
          ]
        : []),
      {
        key: 'see-event',
        label: t('notifications.actions.seeEvent'),
        icon: 'calendar',
        onPress: withRead(openEvent),
      },
    ]
  } else if (isScheduled) {
    actions = [
      {
        key: 'edit',
        label: t('notifications.actions.edit'),
        icon: 'clock',
        onPress: withRead(() => openScheduledMessage(scheduledData.scheduledMessageId)),
      },
    ]
  } else if (isSent) {
    actions = [
      {
        key: 'see-conversation',
        label: t('notifications.actions.seeConversation'),
        icon: 'messageCircle',
        onPress: withRead(openSentConversation),
      },
    ]
  } else if (isBirthday && personId && isGhost) {
    actions = isEve
      ? [{ key: 'see-card', label: t('notifications.actions.seeCard'), icon: 'user', onPress: withRead(openProfile) }]
      : reachGhost
  } else if (isEve && personId && person) {
    actions = scheduledWish
      ? [
          {
            key: 'edit-wish',
            label: t('notifications.actions.editWish'),
            icon: 'clock',
            onPress: withRead(() => openScheduledMessage(scheduledWish.id)),
          },
        ]
      : [
          {
            key: 'schedule-wish',
            label: t('notifications.actions.scheduleWish'),
            icon: 'clock',
            onPress: () => void scheduleWish(),
          },
        ]
  } else if (isBirthday && personId) {
    actions = [
      {
        key: 'wish',
        label: t('notifications.actions.wish'),
        icon: 'messageCircle',
        onPress: withRead(
          () =>
            void sendBirthdayWish({
              id: personId,
              fullName: name,
              birthDate: birthDate ?? '',
              scope: person?.scope,
            }),
        ),
      },
    ]
  } else if (isPending) {
    actions = [
      { key: 'accept-private', label: t('notifications.actions.acceptPrivate'), onPress: () => onAccept?.(Scope.PRIVATE) },
      { key: 'accept-pro', label: t('notifications.actions.acceptPro'), onPress: () => onAccept?.(Scope.PROFESSIONAL) },
      { key: 'reject', label: t('notifications.actions.reject'), onPress: () => onReject?.() },
    ]
  } else if (isAnswered && personId) {
    actions = [
      {
        key: 'write',
        label: t('notifications.actions.write'),
        icon: 'messageCircle',
        onPress: withRead(() => void openConversation(personId)),
      },
      { key: 'see-profile', label: t('notifications.actions.seeProfile'), icon: 'user', onPress: withRead(openProfile) },
    ]
  }

  const showAvatar = !isEvent && Boolean(person) && !(isSent && sentData.groupId)

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={handlePress}
      style={[styles.card, !notification.isRead && styles.cardUnread]}
      testID={`notification-${type}`}
    >
      <View style={styles.leading}>
        {showAvatar ? (
          <Image
            source={person?.avatarUrl ? { uri: person.avatarUrl } : defaultImage}
            style={styles.avatar}
          />
        ) : (
          <View style={[styles.medallion, { backgroundColor: visual.color + '18' }]}>
            <Icon name={visual.icon} size={theme.iconSize.medium} color={visual.color} />
          </View>
        )}
        {showAvatar && (
          <View
            style={[
              styles.typeBadge,
              { backgroundColor: theme.colors.white, borderColor: visual.color },
            ]}
          >
            <Icon name={visual.icon} size={theme.iconSize.extraSmall} color={visual.color} />
          </View>
        )}
      </View>

      <View style={styles.content}>
        <View style={styles.titleRow}>
          {title}
          {showAgo && (
            <Text type="microText" color={theme.colors.greyBlue} style={styles.time}>
              {t('notifications.ago', {
                time: formatDuration(Math.max(0, dayjs().diff(dayjs(agoOf), 'seconds'))),
              })}
            </Text>
          )}
        </View>

        {isEvent && (
          <View style={styles.participants}>
            <EventParticipants
              participantAccountIds={
                notification.contactId ? [notification.contactId] : notification.contactIds || []
              }
            />
          </View>
        )}

        {when && (
          <View style={[styles.dateChip, late > 0 && styles.lateChip]} testID={late > 0 ? 'notification-late' : undefined}>
            <Icon
              name="clock"
              size={theme.iconSize.extraSmall}
              color={late > 0 ? theme.colors.error : theme.colors.greyBlue}
            />
            <Text type="bodyExtraSmallBold" color={late > 0 ? theme.colors.error : theme.colors.greyBlue}>
              {when}
            </Text>
          </View>
        )}

        {detail}

        <NotificationActions actions={actions} />
      </View>

      {!notification.isRead && <View style={styles.unreadDot} />}

      {isKeep && (
        <PickerModal
          isVisible={isPostponeOpen}
          setIsVisible={setIsPostponeOpen}
          title={t('notifications.postpone.title')}
          value={{ id: '', name: '' }}
          data={[
            { id: 'tomorrow', name: t('notifications.postpone.tomorrow') },
            { id: 'in3Days', name: t('notifications.postpone.in3Days') },
            { id: 'inAWeek', name: t('notifications.postpone.inAWeek') },
            { id: 'inAMonth', name: t('notifications.postpone.inAMonth') },
            { id: 'pick', name: t('notifications.postpone.pickDate') },
          ]}
          onSelect={(item) => {
            if (item.id === 'pick') openKeepContact()
            else onPostpone?.(postponedDate(keepData.keepDate, item.id as PostponeChoice))
          }}
        />
      )}

      {isEvent && eventPeople.length > 1 && (
        <PickerModal
          isVisible={isWarnPickerOpen}
          setIsVisible={setIsWarnPickerOpen}
          title={t('notifications.details.warnWho')}
          value={{ id: '', name: '' }}
          data={eventPeople.map((candidate) => ({ id: candidate.id, name: candidate.fullName || '…' }))}
          onSelect={(item) => warnLate(item.id)}
        />
      )}
    </TouchableOpacity>
  )
}

const AVATAR_SIZE = 52

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.md,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey10,
      overflow: 'hidden',
    },
    cardUnread: {
      backgroundColor: theme.colors.primary + '0A',
      borderColor: theme.colors.primary + '26',
    },
    leading: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      marginRight: theme.spacing.md,
    },
    avatar: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      backgroundColor: theme.colors.grey10,
    },
    medallion: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    typeBadge: {
      position: 'absolute',
      right: -2,
      bottom: -2,
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: theme.colors.white,
    },
    content: {
      flex: 1,
      gap: theme.spacing.sm,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    flexText: {
      flex: 1,
      marginRight: theme.spacing.sm,
    },
    time: {
      marginTop: theme.spacing.xxs,
    },
    participants: {
      marginTop: -theme.spacing.xxs,
    },
    dateChip: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.border.small,
      backgroundColor: theme.colors.grey10,
    },
    lateChip: {
      backgroundColor: theme.colors.error + '14',
    },
    detailRow: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: theme.spacing.xs,
    },
    quote: {
      fontStyle: 'italic',
    },
    unreadDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: theme.colors.primary,
      marginLeft: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
  })
